import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { EmailGuard } from "../App";
import { AuthGuard } from "../App";
import { FaMoneyBillWave, FaHandHoldingUsd, FaBalanceScale, FaUserMinus, FaChartLine } from 'react-icons/fa';
import { Oval } from "react-loader-spinner";
import { StandardURL } from "../App";

// Helper function to format currency
const formatCurrency = (amount) => {
  const numericAmount = parseFloat(amount) || 0;
  return new Intl.NumberFormat('en-KE', { 
    style: 'currency', 
    currency: 'Ksh', 
    minimumFractionDigits: 0 
  }).format(numericAmount).replace('Ksh', 'Ksh ');
};

// Component for a single metric card
const MetricCard = ({ title, value, icon: Icon, colorClass }) => {
    return (
        <div className={`bg-white p-6 rounded-xl shadow-lg border-b-4 ${colorClass}`}>
            <div className="flex items-center justify-between">
                <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider">{title}</h2>
                <Icon className={`w-6 h-6 ${colorClass.replace('border-', 'text-').replace('-600', '-500')}`} />
            </div>
            <p className="mt-4 text-3xl font-extrabold text-gray-900">
                {formatCurrency(value)}
            </p>
        </div>
    );
};

const MoneyOutPage = () => {
  const [totalDeposit, setTotalDeposit] = useState(0);
  const [totalBalance, setTotalBalance] = useState(0);
  const [totalUserBalance, setTotalUserBalance] = useState(0);
  const [totalCombined, setTotalCombined] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = () => {
    setIsLoading(true);

    axios
      .all([
        axios.get(
          `${StandardURL}/iNyumba/moneyout/totaldeposit`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
        axios.get(
          `${StandardURL}/iNyumba/moneyout/totalbalance`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
        axios.get(
          `${StandardURL}/iNyumba/moneyout/totaluserbalance`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
        axios.get(
          `${StandardURL}/iNyumba/moneyout/totalcombined`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
      ])
      .then(
        axios.spread(
          (
            totalDepositRes,
            totalBalanceRes,
            totalUserBalanceRes,
            totalCombinedRes
          ) => {
            // Use fallback to 0 in case the API returns null or non-numeric response
            setTotalDeposit(totalDepositRes.data.response || 0);
            setTotalBalance(totalBalanceRes.data.response || 0);
            setTotalUserBalance(totalUserBalanceRes.data.response || 0);
            setTotalCombined(totalCombinedRes.data.response || 0);
            setIsLoading(false);
          }
        )
      )
      .catch((error) => {
        console.error("Error fetching data:", error);
        toast.error(error.response?.data?.response || "Error fetching money out data.");
        setIsLoading(false);
      });
  };

  return (
    <EmailGuard>
      <AuthGuard>
        <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">          
          <div className="max-w-7xl mx-auto">
            <h1 className="text-3xl font-extrabold text-gray-900 flex items-center mb-8">
                <FaChartLine className="mr-3 text-red-600" /> Financial Overview (Money Out)
            </h1>

            {isLoading ? (
                <div className="flex flex-col items-center justify-center p-12 bg-white rounded-xl shadow-xl">
                    <Oval color="#ef4444" secondaryColor="#fca5a5" height={50} width={50} ariaLabel="loading-spinner" />
                    <p className="mt-4 text-gray-600">Fetching financial liability metrics...</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    
                    {/* Total Combined - Highlighted */}
                    <div className="lg:col-span-4">
                        <MetricCard
                            title="Total Financial Liability (Combined)"
                            value={totalCombined}
                            icon={FaHandHoldingUsd}
                            colorClass="border-red-600 bg-red-50 shadow-2xl"
                        />
                    </div>
                    
                    {/* Individual Liability Cards */}
                    <MetricCard
                        title="Total Deposit (Liability)"
                        value={totalDeposit}
                        icon={FaMoneyBillWave}
                        colorClass="border-indigo-600"
                    />
                    
                    <MetricCard
                        title="Total Outstanding Balance"
                        value={totalBalance}
                        icon={FaBalanceScale}
                        colorClass="border-yellow-600"
                    />
                    
                    <MetricCard
                        title="Total User Balance (Owed to Users)"
                        value={totalUserBalance}
                        icon={FaUserMinus}
                        colorClass="border-green-600"
                    />

                    {/* Placeholder Card */}
                    <div className="bg-white p-6 rounded-xl shadow-lg border-b-4 border-gray-300 flex flex-col justify-center">
                        <p className="text-center text-gray-400 font-medium">Detailed payout reports available soon...</p>
                    </div>

                </div>
            )}
          </div>
        </div>
      </AuthGuard>
    </EmailGuard>
  );
};

export default MoneyOutPage;